import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { ReleaseNote } from '../core/classes/release-note';
import { Territory } from '../core/classes/territory';
import { Report } from '../pages/reports/classes/report';
import { ReportGroup } from '../pages/reports/classes/report-group';
import { Coverage } from '../shared/classes/coverage';
import { RestorationMetric } from '../shared/classes/restoration-metric';
import { RestorationPoint } from '../shared/classes/restoration-point';

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  private readonly baseUrl = 'api/report';
  private http = inject(HttpClient);
  private cache = new Map<string, Observable<unknown>>();

  constructor() {
  }

  public fetchReports(): Observable<ReportGroup[]> {
    return this.http.get<ReportGroup[]>(`${this.baseUrl}/groups`).pipe(
      map(groups => groups.map(group => new ReportGroup(group))),
    );
  }

  public getReleaseNotes(): Observable<ReleaseNote[]> {
    const url = 'api/release-note';
    return this.http.get<ReleaseNote[]>(url).pipe(
      map((notes) => {
        return notes.map(note => new ReleaseNote(note));
      }),
    );
  }


  public getReportByServiceUrl(serviceUrl: string): Observable<Report> {
    const url = `${this.baseUrl}?service=${serviceUrl}`;
    if (this.cache.has(url)) {
      return this.cache.get(url) as Observable<Report>;
    }
    const request$ = this.http.get<Report>(url).pipe(
      map(response => new Report(response)),
      shareReplay(1),
    );
    this.cache.set(url, request$);
    return request$;
  }

  public getRestorationMetrics(years: number[], states: Territory[]): Observable<RestorationMetric[]> {
    const url = 'api/metrics/base';
    const params = new HttpParams()
      .set('years', years.join())
      .set('states', states.map(t => t.abbreviation).join());

    return this.http.get<RestorationMetric[]>(url, { params }).pipe(
      map(metrics => metrics.map(metric => new RestorationMetric(metric))),
    );
  }

  public getRestorationCurveData(outageId: string): Observable<RestorationPoint[]> {
    const url = `api/curve/${outageId}`;
    return this.http.get<RestorationPoint[]>(url).pipe(
      map(curves => curves.map(curve => new RestorationPoint(curve))),
    );
  }

  public getStateCoverageData(state?: Territory[]): Observable<Coverage[]> {
    const url = 'api/coverage/states';
    let params = new HttpParams();
    const stateParam = state?.map(s => s.abbreviation).join(',');

    if (stateParam) {
      params = params.set('state', stateParam);
    }
    const cacheKey = `${url}-${stateParam ?? 'all'}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as Observable<Coverage[]>;
    }

    const request$ = this.http.get<Coverage[]>(url, { params }).pipe(
      map(coverage => coverage),
      shareReplay(1),
    );
    this.cache.set(cacheKey, request$);
    return request$;

  }

  public getCountyCoverageData(year: number, state: Territory[]): Observable<Coverage[]> {
    const url = `api/coverage/counties/${year}`;
    const stateParam = state.map(s => s.abbreviation).join(',');
    const params = new HttpParams().set('state', stateParam);
    const cacheKey = `${url}-${stateParam}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as Observable<Coverage[]>;
    }
    const request$ = this.http.get<Coverage[]>(url, { params }).pipe(
      map(counties => counties),
      shareReplay(1),
    );
    this.cache.set(cacheKey, request$);
    return request$;
  }

}
